import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { AdvancedFormControl } from "@magieno/angular-advanced-forms";
import { LicenseTypeEnum } from '../../../../../../../../common/src/enums/license-type.enum';
import { BillingApiController } from '../../../api-controllers/billing.api-controller';
import { DateUtils } from '../../../../../../modules/src/lib/core/utils/date.utils';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { CreateSubscriptionFormFactory } from '../create/create-subscription-form.factory';
import { AdvancedForm } from "@magieno/angular-advanced-forms";
import {
  CalculateSubscriptionOptions,
} from '../../../../../../../../common/src/options/calculate-subscription.options';
import { Subscription } from 'rxjs';
import {
  StripePaymentMethodModel,
} from '../../../../../../../../common/dist/angular/models/stripe-payment-method.model';
import { ViewStateEnum } from '../../../enums/view-state.enum';
import {
  CreatePaymentMethodModalComponent,
} from '../../../components/create-payment-method-modal/create-payment-method-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BillingManager } from '../../../managers/billing.manager';
import { MoneyUtil } from '../../../../../../../../common/src/utils/money.util';
import { SubscriptionUtils } from '../../../../../../../../common/src/utils/subscription.utils';
import { v4 as uuid } from 'uuid';
import { AlertMessageStore } from '../../../../../../modules/src/lib/core/stores/alert-message.store';
import { AlertMessageModel } from '../../../../../../modules/src/lib/core/models/alert-message.model';
import { SeverityTypeEnum } from '../../../../../../modules/src/lib/core/enums/severity-type.enum';
import { AlertContextEnum } from '../../../../../../modules/src/lib/core/enums/alert-context.enum';
import { BillingCycleEnum } from '../../../../../../../../common/src/enums/billing-cycle.enum';
import { CacheStorageService } from '../../../../../../modules/src/lib/core/services/cache-storage.service';

declare global {
  interface Window {
    Stripe: any;
  }
}

@Component({
  selector: 'app-subscription-upgrade',
  templateUrl: './subscription-upgrade.component.html',
  styleUrls: ['./subscription-upgrade.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class SubscriptionUpgradeComponent implements OnInit, OnDestroy {
  calculateSubscriptionForm: AdvancedForm<CalculateSubscriptionOptions>;

  subtotal: number = 199.00;

  licenseTypeSubscription: Subscription;

  public paymentMethods: StripePaymentMethodModel[] = [];

  public selectedPaymentMethod: StripePaymentMethodModel;

  public paymentMethodState: ViewStateEnum = ViewStateEnum.Loading;

  public amountState: ViewStateEnum = ViewStateEnum.Loading;

  public currentLicenseState: ViewStateEnum = ViewStateEnum.Loading;

  currentLicense: LicenseTypeEnum;
  selectedLicense: LicenseTypeEnum;

  public idempotencyKey: string;

  billingCycle: BillingCycleEnum = BillingCycleEnum.Monthly;

  constructor(private readonly stripeApiController: BillingApiController,
              private readonly dateUtils: DateUtils,
              public readonly translateService: TranslateService,
              private readonly router: Router,
              private readonly modalService: NgbModal,
              private readonly billingManager: BillingManager,
              private readonly alertMessageStore: AlertMessageStore,
              private readonly createSubscriptionFormFactory: CreateSubscriptionFormFactory,
              private readonly cacheStorageService: CacheStorageService,
  ) {
    this.calculateSubscriptionForm = this.createSubscriptionFormFactory.createCalculateSubscription();
  }

  ngOnDestroy(): void {
  }

  async ngOnInit() {
    this.idempotencyKey = uuid();
    await this.cacheStorageService.clear();

    await this.refreshPaymentMethod();

    const licenseTypeFormControl = this.getLicenseTypeFormControl();

    this.licenseTypeSubscription = licenseTypeFormControl.value$.subscribe(async value => {
      await this.onLicenseSelect(value.value);
    });

    await this.refreshCurrentLicense();

    await licenseTypeFormControl.setValue(this.currentLicense);
  }

  async refreshPaymentMethod() {
    // Reset the idempotency key on actions
    this.idempotencyKey = uuid();
    this.paymentMethodState = ViewStateEnum.Loading;

    this.paymentMethods = await this.billingManager.getPaymentMethods();
    this.paymentMethodState = ViewStateEnum.Ready;

    // todo: fix this to select a more ideal card
    if (this.paymentMethods.length > 0) {
      this.selectedPaymentMethod = this.paymentMethods[0];
    }
  }

  async refreshCurrentLicense() {
    this.currentLicenseState = ViewStateEnum.Loading;

    this.currentLicense = (await this.billingManager.getLicense(true)).licenseType;

    this.currentLicenseState = ViewStateEnum.Ready;
  }

  async onLicenseSelect(licenseType: LicenseTypeEnum) {
    // Reset the idempotency key on actions
    this.idempotencyKey = uuid();

    if (licenseType !== undefined && SubscriptionUtils.isADowngradeFrom(this.currentLicense, licenseType)) {
      this.alertMessageStore.save(new AlertMessageModel(SeverityTypeEnum.Error, AlertContextEnum.Global, {
        fr: 'Pour rétrograder un abonnement, veuillez nous contacter en utilisant l\'icône de clavardage dans le coin en bas à droite..',
        en: 'To downgrade a subscription, please contact us using the chat in the bottom right corner.',
      }));

      await this.getLicenseTypeFormControl().setValue(this.currentLicense);

      return;
    }

    this.amountState = ViewStateEnum.Refreshing;
    this.billingCycle = undefined;
    const response = await this.stripeApiController.calculateSubscription({ licenseType: licenseType });
    this.billingCycle = response.billingCycle;
    this.subtotal = MoneyUtil.centsToDollars(response.subtotal);
    this.amountState = ViewStateEnum.Ready;
    this.selectedLicense = licenseType;
  }

  showAddPaymentMethodModal() {
    const modalRef = this.modalService.open(CreatePaymentMethodModalComponent);
    modalRef.result.then(async result => {
      await this.refreshPaymentMethod();
    });
  }

  selectPaymentMethod(paymentMethod: StripePaymentMethodModel) {
    this.selectedPaymentMethod = paymentMethod;
  }

  getLicenseTypeFormControl(): AdvancedFormControl<LicenseTypeEnum, LicenseTypeEnum, string> {
    return this.calculateSubscriptionForm.formElements.licenseType as AdvancedFormControl<LicenseTypeEnum, LicenseTypeEnum, string>;
  }

  async submit() {
    try {
      if (this.selectedPaymentMethod === undefined) {
        this.alertMessageStore.save(new AlertMessageModel(SeverityTypeEnum.Error, AlertContextEnum.Global, {
          fr: 'Vous devez sélectionner une carte de crédit pour continuer.',
          en: 'You must select a credit card to continue.',
        }));
        return;
      }

      await this.stripeApiController.finalizeSubscription({
        setupFreeTrial: false,
        licenseType: this.selectedLicense,
        paymentMethodId: this.selectedPaymentMethod.id,
      }, this.idempotencyKey);

      this.alertMessageStore.save(new AlertMessageModel(SeverityTypeEnum.Success, AlertContextEnum.Global, {
        fr: 'Abonnement mis à jours avec succès.',
        en: 'Subscription successfully updated.',
      }));

      await this.router.navigateByUrl('/organization/billing');
    } catch (e) {
      const message = e.error.message;

      if (message == 'Cannot setup the subscription while a subscription is already active.') {
        this.alertMessageStore.save(new AlertMessageModel(SeverityTypeEnum.Error, AlertContextEnum.Global, {
          fr: 'Vous avez déjà un abonnement actif. Veuillez choisir un forfait supérieur afin de continuer.',
          en: 'You already have an active subscription. Please select a higher subscription to continue.',
        }));

        return;
      }

      if (message.startsWith('Cannot add multiple subscription items with the same plan')) {
        this.alertMessageStore.save(new AlertMessageModel(SeverityTypeEnum.Info, AlertContextEnum.Global, {
          fr: 'Si vous avez ajouté une carte de crédit, votre abonnement sera débité automatiquement à la fin de votre période d\'essai',
          en: 'If you have entered a credit card, your subscription will automatically be billed automatically at the end of your free trial',
        }));
        return;
      }

      this.alertMessageStore.save(new AlertMessageModel(SeverityTypeEnum.Error, AlertContextEnum.Global, {
        fr: e.error.message,
        en: e.error.message,
      }));
    }

  }
}
